import React, { FC, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { EAssetType } from 'constants/enums';
import { Info } from 'lucide-react';
import { ProjectCategory } from 'views/Projects/constants';

import { Tooltip } from 'components/Tooltip/Tooltip';

import Button from '../Button/Button';
import { cn } from '../lib/utils';
import Typography from '../Typography/Typography';
import { Dialog, DialogOverlay, DialogTrigger } from '../ui/dialog';

import UploadImageModal from './UploadImageModal/UploadImageModal';
import { UploadImagePreview } from './UploadImagePreview';

type UploadImageProps = {
  title?: string;
  description?: string;
  buttonText?: string;
  className?: string;
  onUpload?: (url: string, name: string, mimeType: string) => void;
  isRequired?: boolean;
  selectedImageUrl?: string;
  modalOnly?: boolean;
  uploadOnly?: boolean;
  isModalOpen?: boolean;
  externalModalOpenFunction?: (open: boolean) => void;
  forceType?: EAssetType;
  uploadContext?: ProjectCategory;
  hideSuccessToast?: boolean;
  'data-testid'?: string;
  buttonDataTestId?: string;
  labelClassName?: string;
  TooltipContent?: React.FC;
};

const UploadImage: FC<UploadImageProps> = ({
  title = '',
  description = '',
  buttonText = 'Upload',
  className,
  onUpload,
  isRequired = false,
  selectedImageUrl = '',
  modalOnly = false,
  uploadOnly = false,
  isModalOpen = false,
  externalModalOpenFunction,
  forceType,
  'data-testid': dataTestId,
  buttonDataTestId,
  labelClassName,
  TooltipContent
}) => {
  const [isOpen, setIsOpen] = useState(isModalOpen);

  const handleUpload = (url: string, name: string, mimeType: string) => {
    onUpload?.(url, name, mimeType);
  };

  const handleRemoveImage = () => {
    onUpload?.('', '', '');
  };

  useEffect(() => {
    if (externalModalOpenFunction && isOpen !== isModalOpen) {
      externalModalOpenFunction(isOpen);
    }
  }, [isOpen, externalModalOpenFunction, isModalOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogOverlay className="bg-alpha-80" />
      <div
        data-testid={dataTestId}
        style={{ display: modalOnly && !isOpen ? 'none' : 'block' }}
        className={cn(
          'flex justify-between items-center w-[427px] py-8 px-11 bg-base-secondary rounded-md border-dashed border border-base-mutedForeground',
          selectedImageUrl && 'py-2.5 px-6',
          className
        )}
      >
        <div>
          <Typography
            className={cn(
              'flex items-center font-medium text-p-s mb-1.5',
              labelClassName
            )}
          >
            {TooltipContent && (
              <div className="mr-2">
                <Tooltip content={<TooltipContent />} className="z-[1000]">
                  <div className="cursor-pointer text-base-mutedForeground">
                    <Info size={16} className="cursor-pointer" />
                  </div>
                </Tooltip>
              </div>
            )}
            {isRequired && (
              <div className="mr-1 mt-1">
                <FontAwesomeIcon
                  icon="fa-regular fa-asterisk"
                  className="h-2 w-2 text-[8px]"
                />
              </div>
            )}
            {title}
          </Typography>
          <Typography className="text-p-xs text-base-sidebarRing whitespace-pre-line">
            {description}
          </Typography>
        </div>
        <DialogTrigger asChild>
          {selectedImageUrl ? (
            <UploadImagePreview
              preview={selectedImageUrl}
              handleRemoveImage={handleRemoveImage}
            />
          ) : (
            <Button isUploadButton data-testid={buttonDataTestId}>
              {buttonText}
            </Button>
          )}
        </DialogTrigger>
        <UploadImageModal
          onClose={() => setIsOpen(false)}
          onUpload={handleUpload}
          selectedImageUrl={selectedImageUrl}
          forceType={forceType}
          uploadOnly={uploadOnly}
        />
      </div>
    </Dialog>
  );
};

export default UploadImage;
